import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Sliders } from "lucide-react";
import { Pagination, usePagination } from "@/components/Pagination";

const KPI_TYPES = [
  { v: "presentasi", label: "Presentasi" },
  { v: "principal_relationship", label: "Principal Relationship" },
  { v: "technical_proposal", label: "Technical Proposal" },
  { v: "sertifikasi", label: "Sertifikasi" },
  { v: "self_skill", label: "Self Skill Development" },
];

export default function Performance() {
  const { user, isReadOnly } = useAuth();
  const isLeader = user?.role === "team_leader";
  const [items, setItems] = useState([]);
  const [weights, setWeights] = useState(null);
  const [form, setForm] = useState({ kpi_type: "presentasi", date: new Date().toISOString().slice(0, 10), amount: 1, note: "" });
  const [openWeights, setOpenWeights] = useState(false);
  const [pageItems, ctrl] = usePagination(items, 10);

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/performance"), api.get("/kpi/weights")]);
    setItems(a.data);
    setWeights(b.data);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/performance", { ...form, amount: Number(form.amount) });
      toast.success("Entry ditambahkan");
      setForm({ ...form, amount: 1, note: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    await api.delete(`/performance/${id}`);
    toast.success("Terhapus");
    load();
  };

  const saveWeights = async (e) => {
    e.preventDefault();
    try {
      const payload = {};
      KPI_TYPES.forEach(({ v }) => { payload[v] = Number(weights[v] || 0); });
      await api.put("/kpi/weights", payload);
      toast.success("Bobot KPI tersimpan");
      setOpenWeights(false);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-8" data-testid="performance-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-mini">Performance</div>
          <h1 className="mt-1 font-heading text-4xl font-black tracking-tight">Input & Track KPI</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Catat aktivitas KPI harian Anda pada 5 dimensi presales.
          </p>
        </div>
        {isLeader && (
          <Dialog open={openWeights} onOpenChange={setOpenWeights}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-sm" data-testid="open-weights-button">
                <Sliders className="h-4 w-4 mr-2" /> Bobot KPI
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Bobot KPI (Total tidak wajib 100)</DialogTitle></DialogHeader>
              <form onSubmit={saveWeights} className="space-y-3">
                {KPI_TYPES.map(({ v, label }) => (
                  <div key={v} className="grid grid-cols-3 items-center gap-3">
                    <Label className="col-span-2">{label}</Label>
                    <Input
                      type="number" min={0} max={100}
                      value={weights?.[v] ?? 0}
                      onChange={(e) => setWeights({ ...weights, [v]: e.target.value })}
                      data-testid={`weight-${v}`}
                    />
                  </div>
                ))}
                <Button type="submit" className="w-full rounded-sm" data-testid="save-weights-button">Simpan</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Form */}
      {!isReadOnly && (
      <form onSubmit={submit} className="card-tech p-6 grid grid-cols-1 md:grid-cols-5 gap-4" data-testid="performance-form">
        <div className="md:col-span-2">
          <Label className="label-mini">Jenis KPI</Label>
          <Select value={form.kpi_type} onValueChange={(v) => setForm({ ...form, kpi_type: v })}>
            <SelectTrigger data-testid="kpi-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {KPI_TYPES.map((k) => <SelectItem key={k.v} value={k.v}>{k.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-mini">Tanggal</Label>
          <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="kpi-date-input" />
        </div>
        <div>
          <Label className="label-mini">Jumlah</Label>
          <Input type="number" min={0} step="0.5" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="kpi-amount-input" />
        </div>
        <div className="flex items-end">
          <Button type="submit" className="w-full rounded-sm" data-testid="add-kpi-button">
            <Plus className="h-4 w-4 mr-2" /> Tambah
          </Button>
        </div>
        <div className="md:col-span-5">
          <Label className="label-mini">Catatan</Label>
          <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="opsional" data-testid="kpi-note-input" />
        </div>
      </form>
      )}

      {/* List */}
      <div className="card-tech p-0 overflow-hidden">
        <div className="p-6 border-b border-border">
          <div className="label-mini">Riwayat</div>
          <h3 className="font-heading text-xl font-bold mt-1">Entry KPI</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-accent text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left px-6 py-3">Tanggal</th>
                <th className="text-left px-6 py-3">KPI</th>
                <th className="text-right px-6 py-3">Jumlah</th>
                <th className="text-left px-6 py-3">Catatan</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={5} className="px-6 py-10 text-center text-muted-foreground">Belum ada entry KPI.</td></tr>
              )}
              {pageItems.map((it) => (
                <tr key={it.id} className="border-t border-border hover:bg-accent/50" data-testid={`perf-row-${it.id}`}>
                  <td className="px-6 py-3 mono">{it.date}</td>
                  <td className="px-6 py-3">{KPI_TYPES.find((k) => k.v === it.kpi_type)?.label || it.kpi_type}</td>
                  <td className="px-6 py-3 text-right mono">{it.amount}</td>
                  <td className="px-6 py-3 text-muted-foreground">{it.note}</td>
                  <td className="px-6 py-3 text-right">
                    {!isReadOnly && (
                      <Button size="sm" variant="ghost" onClick={() => remove(it.id)} data-testid={`delete-perf-${it.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination ctrl={ctrl} testidPrefix="perf-pg" />
      </div>
    </div>
  );
}
